import type { ReactNode } from "react";
import {
  type MeetingOffsetConfig,
  meetingOffsetOptions,
  triggerConditionLabel,
  triggerParamLabel,
  withMeetingOffset,
} from "@/shared/routines.ts";
import { cn } from "../../lib/utils.ts";
import type { TriggerConfig } from "../../types/routines.ts";
import { CronScheduleEditor } from "../cron/CronScheduleEditor.tsx";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select.tsx";
import { RoutineAmendmentSection } from "./RoutineFormFields.tsx";
import { getTriggerIcon } from "./triggerIcon.ts";

/**
 * The General tab of a routine okthx ships: the line the user reads in the list,
 * what fires the routine, and the user's own instructions.
 *
 * The name, description and prompt are okthx's — they are what makes the routine
 * the routine — so none of them is a field here. The description is still shown,
 * read-only, because it is what the list says this routine does; the prompt is
 * not shown at all, being text the user can neither change nor act on. Schedule
 * and amendment have a per-user override behind them, so editing one writes a
 * preference and the routine keeps tracking okthx's copy.
 */
export const SystemRoutineFields = ({
  description,
  triggerConfig,
  onTriggerConfigChange,
  amendment,
  onAmendmentChange,
}: {
  description: string;
  triggerConfig: TriggerConfig;
  onTriggerConfigChange: (config: TriggerConfig) => void;
  amendment?: string;
  onAmendmentChange?: (amendment: string) => void;
}) => {
  const TriggerIcon = getTriggerIcon(triggerConfig.type);
  const param = triggerParamLabel(triggerConfig);

  return (
    <>
      {/* Omitted rather than shown empty: with nothing to read, a caption over a
          blank line is a field the user would reach for and find inert. Every
          routine okthx ships has one, so this is the defensive branch. */}
      {description && (
        <>
          <div>
            <SectionCaption>Description</SectionCaption>
            <p className="text-sm text-muted-foreground whitespace-pre-wrap">{description}</p>
          </div>

          <div className="h-px bg-border" />
        </>
      )}

      <div>
        <SectionCaption icon={<TriggerIcon className="size-3.5 text-muted-foreground" />}>
          When
        </SectionCaption>
        {triggerConfig.type === "cron" ? (
          <CronScheduleFields config={triggerConfig} onChange={onTriggerConfigChange} />
        ) : (
          <div className="space-y-2">
            {/* Where the trigger-type select sits on a routine of the user's own,
                so the condition still reads first and its parameters under it. */}
            <div className="text-sm font-medium">{triggerConditionLabel(triggerConfig)}</div>
            {isMeetingOffsetTrigger(triggerConfig) ? (
              <FieldRow
                label={
                  triggerConfig.type === "beforeMeeting" ? "How long before" : "How long after"
                }
                labelWidth="w-28"
              >
                <MeetingOffsetSelect config={triggerConfig} onChange={onTriggerConfigChange} />
              </FieldRow>
            ) : (
              param && <div className="text-sm text-muted-foreground">{param}</div>
            )}
          </div>
        )}
      </div>

      <div className="h-px bg-border" />

      <RoutineAmendmentSection amendment={amendment} onChange={onAmendmentChange} />
    </>
  );
};

/**
 * One labelled field, in the caption-then-control shape the routine form uses
 * for every trigger parameter.
 */
const FieldRow = ({
  label,
  labelWidth = "w-16",
  children,
}: {
  label: string;
  labelWidth?: string;
  children: ReactNode;
}) => (
  <div className="flex items-center gap-2">
    <span className={cn("text-xs text-muted-foreground shrink-0", labelWidth)}>{label}</span>
    {children}
  </div>
);

/**
 * A scheduled routine: the same {@link CronScheduleEditor} a routine of the
 * user's own gets, writing through as a per-user schedule override.
 *
 * The whole schedule is the user's to set, repeat pattern included. It used to
 * be only the time, on the reasoning that a briefing firing on weekday mornings
 * *is* the routine — but a user who wants their briefing on Sundays is asking
 * for the routine they already have, at a cadence that suits them, and the
 * routine artifact's own card has always let them say so. `scheduleOverride`
 * stores an arbitrary expression and `applyScheduleOverride` swaps it in
 * wholesale, so nothing under here needed the pattern to stay okthx's.
 */
const CronScheduleFields = ({
  config,
  onChange,
}: {
  config: Extract<TriggerConfig, { type: "cron" }>;
  onChange: (config: TriggerConfig) => void;
}) => (
  <CronScheduleEditor
    value={config.schedule}
    // Spread rather than rebuild: only `schedule` is persisted by the override
    // this feeds, but a rebuilt config would drop `timezone` from the optimistic
    // copy and flash the wrong zone until the refetch lands.
    onChange={(schedule) => onChange({ ...config, schedule })}
    timezone={config.timezone}
  />
);

const isMeetingOffsetTrigger = (config: TriggerConfig): config is MeetingOffsetConfig =>
  config.type === "beforeMeeting" || config.type === "afterMeeting";

/**
 * How far either side of the meeting the routine fires.
 *
 * Unlike the schedule beside it, which writes through on blur, this one lands in
 * the draft and Save appears — the whole trigger config goes over as one object.
 */
const MeetingOffsetSelect = ({
  config,
  onChange,
}: {
  config: MeetingOffsetConfig;
  onChange: (config: TriggerConfig) => void;
}) => {
  const offset = config.type === "beforeMeeting" ? config.minutesBefore : config.minutesAfter;

  const handleChange = (next: string) => onChange(withMeetingOffset(config, Number(next)));

  return (
    <Select value={String(offset)} onValueChange={handleChange}>
      <SelectTrigger className="h-8 text-sm w-28 shrink-0 rounded-md">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {meetingOffsetOptions(config.type, offset).map((option) => (
          <SelectItem key={option.value} value={String(option.value)}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

const SectionCaption = ({ icon, children }: { icon?: ReactNode; children: ReactNode }) => (
  <div className="flex items-center gap-2 mb-3">
    {icon}
    <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
      {children}
    </span>
  </div>
);
